import type React from 'react';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';

import { Button } from '@change/ds-core-ui/Button';
import { Card, CardBody, CardHeader } from '@change/ds-core-ui/Card';
import { Separator } from '@change/ds-core-ui/Separator';
import { Text } from '@change/ds-core-ui/Text';
import { ArrowRightFilledIcon } from '@change/ds-icons/ArrowRightFilledIcon';

import { Translate } from '@change-corgi/core-react/i18n';
import { Redirect } from '@change-corgi/core-react/router';

import { ARTICLE_CONTENT_OPTIONS } from '#src/app/pages/petitionGuides/article/shared/consts';

import { CtaSection } from './components/CtaSection';
import { ExploreArticlesSection } from './components/ExploreArticlesSection';
import { LearnHowSection } from './components/LearnHowSection';
import { UsefulArticlesSection } from './components/UsefulArticlesSection';
import { usePageBody } from './hooks/usePageBody';
import { scrollToContent } from './scrollToContent';

export const PageBody: React.FC<EmptyProps> = () => {
	const {
		data: { contentLinks, content, redirectUrl },
	} = usePageBody();

	const articleContentToComponents = documentToReactComponents(content, ARTICLE_CONTENT_OPTIONS);

	if (redirectUrl) {
		return <Redirect to={redirectUrl} />;
	}

	return (
		<>
			<div className="container mt-6 mb-5 grid gap-6 md:mt-16 md:grid-cols-8 md:gap-16">
				<div className="order-last md:order-first md:col-span-3">
					<div className="top-6 space-y-6 md:sticky md:h-screen md:space-y-10 md:overflow-y-auto md:pb-6">
						<LearnHowSection />
						<ExploreArticlesSection />
						<CtaSection />
					</div>
				</div>

				<div className="md:col-span-5">
					{contentLinks.length > 0 && (
						<Card className="mb-6 p-1 md:mb-10">
							<CardHeader>
								<Text as="h2" variant="heading" size="x-small">
									<Translate value="corgi.petition_guides_article.page_body.article_links_title" />:
								</Text>
							</CardHeader>

							<CardBody className="flex flex-col gap-4">
								{contentLinks.map(({ id, displayText }) => (
									<Button
										key={id}
										data-qa={`${displayText}-content-link`}
										onClick={id ? () => scrollToContent(id) : undefined}
										leadingIcon={<ArrowRightFilledIcon />}
										variant="tertiary"
										size="small"
										className="text-typography-interactive! justify-start p-0 hover:bg-transparent! focus:bg-transparent!"
									>
										<Text>{displayText}</Text>
									</Button>
								))}
							</CardBody>
						</Card>
					)}

					{/* The following classnames ensure spacing is consistent between elements,
					while keeping spacing between p + p `24px` and everything else `40px`.
					 */}
					<div className="[&>p+h3]:mt-4 [&>p+hr]:mt-4 [&>p+img]:mt-4">{articleContentToComponents}</div>
					<Separator className="border-stroke-weaker m-0 md:hidden" />
				</div>
			</div>

			<UsefulArticlesSection />
		</>
	);
};
